import React, { useState, useEffect, useCallback } from "react";
import { Button, InputNumber, message, Row, Col } from "antd";
import ModalCommon from "../../components/Modal";
import { TEXT_DEF } from "../../constant/text";
import API, { CONFIG, UPDATE_CONFIG } from "../../network/api";
import { VIP_LEVELS } from "../../constant/vip";

/** Key API: tỷ lệ quy đổi điểm thưởng (thập phân 0–1, ví dụ 0.85 = 85%) */
const GHOST_RATE = "GHOST_RATE";

const DEFAULT_VIP_CONFIG = {
  NEW: { maxScore: 18, percent: 85 },
  NORMAL: { maxScore: 20, percent: 86 },
  COPPER: { maxScore: 22, percent: 87 },
  SILVER: { maxScore: 25, percent: 88 },
  GOLD: { maxScore: 28, percent: 89 },
  PLATINUM: { maxScore: 31, percent: 90 },
  EMERALD: { maxScore: 33, percent: 91 },
  SAPPHIRE: { maxScore: 36, percent: 92 },
  DIAMOND: { maxScore: 38, percent: 93 },
};

const DEFAULT_GAMEPLAY = {
  turnDuration: 45,
  carSpeed: 2,
  totalSymbols: 30,
  itemSpeed: 3,
  preWithdrawRounds: 8,
};

const GP_TEXT = {
  vipSectionTitle: "Cấp VIP & Tỷ lệ điểm thưởng",
  secondsPerTurn: "Số giây mỗi lượt",
  carSpeed: "Tốc độ rổ",
  totalSymbolsLabel: "Số lượng vật phẩm",
  itemSpeed: "Tốc độ vật phẩm",
  preWithdrawRounds: "Vòng cược trước khi rút",
};

const SetupScore = () => {
  const [isShowModalConfirm, setIsShowModalConfirm] = useState(false);
  const [isShowModalSuccess, setIsShowModalSuccess] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const [vipConfigData, setVipConfigData] = useState(DEFAULT_VIP_CONFIG);
  const [preWithdrawRounds, setPreWithdrawRounds] = useState(DEFAULT_GAMEPLAY.preWithdrawRounds);
  const [turnDuration, setTurnDuration] = useState(DEFAULT_GAMEPLAY.turnDuration);
  const [carSpeed, setCarSpeed] = useState(DEFAULT_GAMEPLAY.carSpeed);
  const [totalSymbols, setTotalSymbols] = useState(DEFAULT_GAMEPLAY.totalSymbols);
  const [itemSpeed, setItemSpeed] = useState(DEFAULT_GAMEPLAY.itemSpeed);
  const [originalConfig, setOriginalConfig] = useState({});

  const getDataConfig = useCallback(async () => {
    try {
      const res = await API.get(`${CONFIG}`);
      const data = res.data?.data || res.data;
      setOriginalConfig(data);

      if (data.preWithdrawRounds !== undefined) {
        setPreWithdrawRounds(data.preWithdrawRounds);
      }

      const vipLevels = data.vipLevels || {};
      const newVipConfig = {};
      VIP_LEVELS.forEach((key) => {
        const vipData = vipLevels[key];
        const fallback = DEFAULT_VIP_CONFIG[key] || { maxScore: 0, percent: 0 };
        const maxScore = vipData?.MAX_SCORE ?? fallback.maxScore;
        const rateDecimal = vipData?.[GHOST_RATE];
        const percent =
          rateDecimal !== undefined && rateDecimal !== null
            ? Math.round(rateDecimal * 100)
            : fallback.percent;

        newVipConfig[key] = { maxScore, percent };
      });
      setVipConfigData(newVipConfig);

      const legacyGp = data.gameplay || {};
      const loadedTurn =
        data.turnDuration ??
        data.secondsPerTurn ??
        legacyGp.turnDuration ??
        legacyGp.secondsPerTurn ??
        DEFAULT_GAMEPLAY.turnDuration;
      setTurnDuration(Number(loadedTurn) || DEFAULT_GAMEPLAY.turnDuration);

      const loadedCarSpeed = data.carSpeed ?? DEFAULT_GAMEPLAY.carSpeed;
      setCarSpeed(Number(loadedCarSpeed) || DEFAULT_GAMEPLAY.carSpeed);

      const loadedSymbols = data.totalSymbols ?? DEFAULT_GAMEPLAY.totalSymbols;
      setTotalSymbols(Number(loadedSymbols) || DEFAULT_GAMEPLAY.totalSymbols);

      const loadedItemSpeed = data.itemSpeed ?? DEFAULT_GAMEPLAY.itemSpeed;
      setItemSpeed(Number(loadedItemSpeed) || DEFAULT_GAMEPLAY.itemSpeed);
    } catch (err) {
      console.log(err);
      messageApi.error("Không thể tải dữ liệu cấu hình");
    }
  }, [messageApi]);

  useEffect(() => {
    getDataConfig();
  }, [getDataConfig]);

  const handleUpdateConfig = async () => {
    try {
      setIsShowModalConfirm(false);

      const newVipLevels = {};
      Object.keys(vipConfigData).forEach((key) => {
        const item = vipConfigData[key];
        const existingVipData = originalConfig.vipLevels?.[key] || {};
        const minScore = existingVipData.MIN_SCORE || 0.1;
        const rateDecimal = item.percent > 0 ? Number((item.percent / 100).toFixed(2)) : 0;

        newVipLevels[key] = {
          MAX_SCORE: item.maxScore || 0,
          MIN_SCORE: minScore,
          [GHOST_RATE]: rateDecimal,
        };
      });

      const {
        gameplay: _omitGameplay,
        speeds: _omitSpeeds,
        phasesDuration: _omitPhasesDuration,
        ...configBase
      } = originalConfig;
      const newConfig = {
        ...configBase,
        preWithdrawRounds: Number(preWithdrawRounds) || 0,
        vipLevels: newVipLevels,
        turnDuration: Number(turnDuration) || 0,
        carSpeed: Number(carSpeed) || 0,
        totalSymbols: Number(totalSymbols) || 0,
        itemSpeed: Number(itemSpeed) || 0,
      };

      await API.patch(`${UPDATE_CONFIG}`, { config: newConfig });
      setIsShowModalSuccess(true);

      setTimeout(() => {
        getDataConfig();
      }, 1000);
    } catch (err) {
      console.log(err);
      messageApi.error(err?.response?.data?.message || "Cập nhật thất bại");
      setIsShowModalSuccess(false);
    }
  };

  const handleShowModal = () => setIsShowModalConfirm(true);

  const handleVipChange = (key, field, value) => {
    setVipConfigData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: Number(value) || 0,
      },
    }));
  };

  const renderGameplayRow = (label, input) => (
    <Row gutter={24} style={{ marginBottom: 16, alignItems: "center" }}>
      <Col span={8} style={{ fontWeight: 600 }}>
        {label}
      </Col>
      <Col span={16}>{input}</Col>
    </Row>
  );

  return (
    <div className="col-12" style={{ padding: "20px" }}>
      {contextHolder}

      <div style={{ marginBottom: 32, border: "1px solid #d9d9d9", padding: 20, borderRadius: 8 }}>
        <div style={{ fontWeight: "bold", marginBottom: 20, fontSize: 16 }}>
          {GP_TEXT.vipSectionTitle}
        </div>

        <Row gutter={24} style={{ marginBottom: 15, fontWeight: "bold", textTransform: "uppercase" }}>
          <Col span={6} />
          <Col span={9}>ĐIỂM THƯỞNG MAX</Col>
          <Col span={9}>% TỶ LỆ MA</Col>
        </Row>

        {VIP_LEVELS.map((key) => (
          <Row key={key} gutter={24} style={{ marginBottom: 15, alignItems: "center" }}>
            <Col span={6} style={{ fontWeight: 500 }}>
              {key}:
            </Col>
            <Col span={9}>
              <InputNumber
                style={{ width: "100%" }}
                value={vipConfigData[key]?.maxScore}
                onChange={(val) => handleVipChange(key, "maxScore", val)}
                placeholder="Nhập điểm max"
              />
            </Col>
            <Col span={9}>
              <InputNumber
                style={{ width: "100%" }}
                value={vipConfigData[key]?.percent}
                onChange={(val) => handleVipChange(key, "percent", val)}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace("%", "")}
                min={0}
                max={100}
                placeholder="Nhập %"
              />
            </Col>
          </Row>
        ))}

        <Row gutter={24} style={{ marginTop: 30, alignItems: "center" }}>
          <Col span={6} style={{ fontWeight: 500 }}>
            {GP_TEXT.preWithdrawRounds}:
          </Col>
          <Col span={9}>
            <InputNumber
              style={{ width: 100 }}
              value={preWithdrawRounds}
              onChange={(val) => setPreWithdrawRounds(Number(val) || 0)}
            />
          </Col>
        </Row>
      </div>

      <div style={{ marginBottom: 32, maxWidth: 480 }}>
        {renderGameplayRow(
          GP_TEXT.secondsPerTurn,
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <InputNumber
              min={1}
              max={600}
              style={{ width: 88 }}
              value={turnDuration}
              onChange={(val) => setTurnDuration(Number(val) || 0)}
            />
            <span>giây</span>
          </div>
        )}
        {renderGameplayRow(
          GP_TEXT.carSpeed,
          <InputNumber
            min={0}
            style={{ width: 120 }}
            value={carSpeed}
            onChange={(val) => setCarSpeed(Number(val) || 0)}
          />
        )}
        {renderGameplayRow(
          GP_TEXT.totalSymbolsLabel,
          <InputNumber
            min={0}
            style={{ width: 120 }}
            value={totalSymbols}
            onChange={(val) => setTotalSymbols(Number(val) || 0)}
          />
        )}
        {renderGameplayRow(
          GP_TEXT.itemSpeed,
          <InputNumber
            min={0}
            style={{ width: 120 }}
            value={itemSpeed}
            onChange={(val) => setItemSpeed(Number(val) || 0)}
          />
        )}
      </div>

      <div style={{ textAlign: "center" }}>
        <Button type="primary" onClick={handleShowModal}>
          Cập nhật
        </Button>
      </div>

      {isShowModalConfirm && (
        <ModalCommon
          isShow={isShowModalConfirm}
          message={TEXT_DEF.text0062}
          txtBtnAcc={TEXT_DEF.text0008}
          textBtnClose={TEXT_DEF.text0023}
          actionNext={handleUpdateConfig}
          close={() => setIsShowModalConfirm(false)}
        />
      )}
      {isShowModalSuccess && (
        <ModalCommon
          isShow={isShowModalSuccess}
          message={TEXT_DEF.text0063}
          txtBtnAcc={TEXT_DEF.text0007}
          actionNext={() => {
            setIsShowModalSuccess(false);
            getDataConfig();
          }}
          close={() => {
            setIsShowModalSuccess(false);
            getDataConfig();
          }}
        />
      )}
    </div>
  );
};

export default SetupScore;
